<template>
  <div class="budget-card" :class="getStatusClass()">
    <div class="budget-header">
      <div class="budget-info">
        <div class="budget-icon">
          <i class="fas fa-calculator"></i>
        </div>
        <div class="budget-details">
          <h4 class="budget-title cabin-family semi-bold">
            {{ (budget && budget.name) || 'My Budget' }}
          </h4>
          <p class="budget-period secondary-text">
            {{ formatPeriod(budget && budget.budget_period) }}
          </p>
        </div>
      </div>

      <div class="budget-status">
        <span :class="`status-badge status-${budget && budget.status}`">
          {{ budget && budget.status }}
        </span>
      </div>
    </div>

    <div class="budget-overview">
      <div class="budget-amounts">
        <div class="amount-item">
          <span class="label secondary-text fnt-10">Total Budget</span>
          <span class="value cabin-family semi-bold">{{ formatCurrency(totalBudgetAmount) }}</span>
        </div>
        <div class="amount-item">
          <span class="label secondary-text fnt-10">Spent</span>
          <span class="value cabin-family semi-bold primary">{{ formatCurrency(totalSpentAmount) }}
          </span>
        </div>
        <div class="amount-item">
          <span class="label secondary-text fnt-10">Remaining</span>
          <span class="value cabin-family semi-bold success">{{ formatCurrency(remainingAmount) }}
          </span>
        </div>
      </div>

      <div class="budget-progress">
        <ProgressBar
          v-if="budgetProgress !== null && budgetProgress !== undefined"
          :percentage="budgetProgress"
          :color="getProgressColor()"
          show-label
          height="10px"
        />
      </div>
    </div>

    <div class="categories-preview">
      <h6 class="categories-title cabin-family semi-bold">Top Categories</h6>
      <div class="categories-list">
        <div
          v-for="category in topCategories"
          :key="category.name"
          class="category-item"
        >
          <div class="category-info">
            <span class="category-name">{{ formatCategoryName(category.name) }}</span>
            <span class="category-progress">
              {{ Math.round(category.progress) }}%
            </span>
          </div>
          <div class="category-bar">
            <div
              class="category-fill"
              :class="getCategoryFillClass(category.progress)"
              :style="{ width: `${Math.min(category.progress, 100)}%` }"
            />
          </div>
          <div class="category-amounts">
            <span class="spent">{{ formatCurrency(category.spent_amount) }}</span>
            <span class="allocated secondary-text">/ {{ formatCurrency(category.allocated_amount) }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <div class="budget-actions">
      <button
        class="btn primary-btn flex-fill me-2"
        @click="$emit('track-spending', budget)"
      >
        Track Spending
      </button>

      <button
        class="btn btn-outline-secondary flex-fill"
        @click="$emit('view-details', budget)"
      >
        View Details
      </button>
    </div>
  </div>
</template>

<script>
import { formatCurrency } from '../../utils/formatters';
import { ProgressBar } from '../shared';

export default {
  name: 'BudgetCard',
  components: {
    ProgressBar,
  },
  props: {
    budget: {
      type: Object,
      required: true,
    },
  },
  computed: {
    totalBudgetAmount() {
      if (!this.budget || !this.budget.categories) return 0;
      return Object.values(this.budget.categories)
        .reduce((total, category) => total + (category.amount || 0), 0);
    },

    // Calculate spending from recent_tracking, budget_trackings or performance data
    categorySpending() {
      if (!this.budget) return {};

      const spending = {};

      // First try to get from performance data
      if (this.budget.performance && this.budget.performance.categories) {
        Object.entries(this.budget.performance.categories).forEach(([category, data]) => {
          spending[category] = data.actual || 0;
        });
        return spending; // Return early if we have performance data
      }

      // Try recent_tracking from new API format
      if (this.budget.recent_tracking && this.budget.recent_tracking.length > 0) {
        this.budget.recent_tracking.forEach((tracking) => {
          if (!spending[tracking.category]) {
            spending[tracking.category] = 0;
          }
          spending[tracking.category] += tracking.amount || 0;
        });
        return spending; // Return early if we have recent_tracking data
      }

      // Fallback to budget_trackings only if no other data available
      if (this.budget.budget_trackings && this.budget.budget_trackings.length > 0) {
        this.budget.budget_trackings.forEach((tracking) => {
          if (!spending[tracking.category]) {
            spending[tracking.category] = 0;
          }
          spending[tracking.category] += tracking.actual_amount || 0;
        });
      }

      return spending;
    },

    totalSpentAmount() {
      const spending = this.categorySpending;
      return Object.values(spending).reduce((total, amount) => total + amount, 0);
    },

    remainingAmount() {
      return Math.max(0, this.totalBudgetAmount - this.totalSpentAmount);
    },

    budgetProgress() {
      if (this.totalBudgetAmount === 0) return 0;
      return Math.round((this.totalSpentAmount / this.totalBudgetAmount) * 100);
    },

    topCategories() {
      if (!this.budget || !this.budget.categories) return [];

      const spending = this.categorySpending;

      return Object.entries(this.budget.categories)
        .map(([name, category]) => {
          const spentAmount = spending[name] || 0;
          const allocatedAmount = category.amount || 0;

          return {
            name,
            allocated_amount: allocatedAmount,
            spent_amount: spentAmount,
            progress: allocatedAmount > 0 ? (spentAmount / allocatedAmount) * 100 : 0,
          };
        })
        .sort((a, b) => b.allocated_amount - a.allocated_amount)
        .slice(0, 3);
    },
  },
  methods: {
    formatCurrency,

    getStatusClass() {
      if (this.budgetProgress >= 100) return 'status-danger';
      if (this.budgetProgress >= 80) return 'status-warning';
      return 'status-success';
    },

    getProgressColor() {
      if (this.budgetProgress >= 100) return 'danger';
      if (this.budgetProgress >= 80) return 'warning';
      return 'success';
    },

    getCategoryFillClass(progress) {
      if (progress >= 100) return 'fill-danger';
      if (progress >= 80) return 'fill-warning';
      return 'fill-success';
    },

    formatPeriod(period) {
      const periodMap = {
        weekly: 'Weekly',
        monthly: 'Monthly',
        quarterly: 'Quarterly',
        yearly: 'Yearly',
      };
      return periodMap[period] || 'Monthly';
    },

    formatCategoryName(name) {
      return name.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../scss/variable.scss';

.budget-card {
  background: $white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  border: 1px solid #E0E0E0;
  margin-bottom: 1rem;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    border-color: $primary;
  }

  &.status-success {
    border-left: 6px solid $success;
  }

  &.status-warning {
    border-left: 6px solid #FF9800;
  }

  &.status-danger {
    border-left: 6px solid #F44336;
  }
}

.budget-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
}

.budget-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
}

.budget-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: $primary-light;
  display: flex;
  align-items: center;
  justify-content: center;
  color: $primary;
  font-size: 20px;
  flex-shrink: 0;
}

.budget-details {
  flex: 1;
  min-width: 0;
}

.budget-title {
  margin: 0 0 0.25rem 0;
  font-size: 16px;
  line-height: 1.2;
  color: $black;
}

.budget-period {
  margin: 0;
  font-size: 12px;
}

.budget-status {
  flex-shrink: 0;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 16px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;

  &.status-active {
    background: rgba($success, 0.2);
    color: $success;
  }

  &.status-inactive {
    background: rgba($secondary, 0.2);
    color: $secondary;
  }

  &.status-completed {
    background: rgba($primary, 0.2);
    color: $primary;
  }
}

.budget-overview {
  margin-bottom: 1.5rem;
}

.budget-amounts {
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding: 1rem;
  background: rgba(248, 249, 250, 0.5);
  border-radius: 8px;
  border: 1px solid #F0F0F0;
}

.amount-item {
  text-align: center;
  flex: 1;
}

.amount-item .label {
  display: block;
  margin-bottom: 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.amount-item .value {
  font-size: 16px;
  color: $black;
}

.categories-preview {
  margin-bottom: 1.5rem;
}

.categories-title {
  margin-bottom: 1rem;
  color: $black;
  font-size: 14px;
}

.categories-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.category-item {
  .category-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.25rem;
  }

  .category-name {
    font-size: 12px;
    color: $black;
    font-weight: 500;
  }

  .category-progress {
    font-size: 12px;
    color: $secondary;
    font-weight: 600;
  }

  .category-bar {
    height: 4px;
    background: #E0E0E0;
    border-radius: 2px;
    overflow: hidden;
    margin-bottom: 0.25rem;
  }

  .category-fill {
    height: 100%;
    border-radius: 2px;
    transition: width 0.3s ease;

    &.fill-success {
      background: $success;
    }

    &.fill-warning {
      background: #FF9800;
    }

    &.fill-danger {
      background: $primary;
    }
  }

  .category-amounts {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 11px;

    .spent {
      color: $black;
      font-weight: 600;
    }

    .allocated {
      color: $secondary;
    }
  }
}

.budget-actions {
  display: flex;
  gap: 0.5rem;
}

.btn-outline-secondary {
  border: 1px solid $secondary;
  color: $secondary;
  background: transparent;

  &:hover {
    background: $secondary;
    color: $white;
  }
}

@media (max-width: 576px) {
  .budget-card {
    padding: 1rem;
  }

  .budget-info {
    gap: 0.75rem;
  }

  .budget-icon {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }

  .budget-title {
    font-size: 14px;
  }

  .budget-amounts {
    flex-direction: column;
    gap: 0.75rem;
    text-align: left;
  }

  .amount-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .amount-item .label {
    margin-bottom: 0;
  }

  .budget-actions {
    flex-direction: column;
    gap: 0.5rem;
  }
}
</style>
